/**
 * ConferenciaCaixaDetailPage Component
 * 
 * Página de detalhes e edição da conferência de caixa.
 * Permite conferir valores por forma de recebimento, comparando valores do sistema,
 * valores informados pelo operador e valores apurados.
 * 
 * Funcionalidades:
 * - Exibir informações gerais do caixa
 * - Tabela editável com três colunas de valores
 * - Cálculo automático de diferenças
 * - Salvar rascunho
 * - Finalizar conferência
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Save, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react'
import Modal from '../../../../components/ERP/Modal/Modal'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import { cashRegisterService } from '../../../../services/erpApi'
import './ConferenciaCaixaDetailPage.css'

function ConferenciaCaixaDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [cashRegister, setCashRegister] = useState(null)
  const [reconciliations, setReconciliations] = useState([])
  const [totals, setTotals] = useState(null)
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [error, setError] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await cashRegisterService.getReconciliation(id)
      if (response.success && response.data) {
        console.log('[ConferenciaCaixa] Dados carregados:', response.data)
        setCashRegister(response.data.cashRegister)
        setReconciliations(response.data.reconciliations || [])
        setTotals(response.data.totals || null)
        
        if (!response.data.reconciliations || response.data.reconciliations.length === 0) {
          console.warn('[ConferenciaCaixa] Nenhuma forma de recebimento encontrada')
        }
      } else {
        setError('Erro ao carregar dados da conferência')
      }
    } catch (error) {
      console.error('Erro ao carregar conferência:', error)
      setError('Erro ao carregar dados da conferência')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const formatTime = (time) => {
    if (!time) return '--:--'
    return time.substring(0, 5) // HH:mm
  }

  const handleValueChange = (paymentMethodId, field, value) => {
    setReconciliations(prev => {
      const updated = prev.map(rec => {
        if (rec.payment_receipt_method_id === paymentMethodId) {
          const newValue = value === '' ? null : parseFloat(value) || 0
          const updatedRec = {
            ...rec,
            [field]: newValue
          }
          
          // Calcular diferença (operator_informed_amount - system_amount)
          if (field === 'operator_informed_amount' || field === 'actual_counted_amount') {
            const operatorAmount = field === 'operator_informed_amount' ? newValue : updatedRec.operator_informed_amount
            const systemAmount = updatedRec.system_amount || 0
            updatedRec.difference = operatorAmount !== null ? operatorAmount - systemAmount : null
          }
          
          return updatedRec
        }
        return rec
      })
      
      // Recalcular totais
      const newTotals = calculateTotals(updated)
      setTotals(newTotals)
      
      return updated
    })
  }

  const calculateTotals = (reconciliations) => {
    const systemTotal = reconciliations.reduce((sum, rec) => sum + (parseFloat(rec.system_amount) || 0), 0)
    const operatorTotal = reconciliations.reduce((sum, rec) => sum + (parseFloat(rec.operator_informed_amount) || 0), 0)
    const actualTotal = reconciliations.reduce((sum, rec) => sum + (parseFloat(rec.actual_counted_amount) || 0), 0)
    
    return {
      system_total: systemTotal,
      operator_total: operatorTotal,
      actual_total: actualTotal,
      differences: {
        operator_vs_system: operatorTotal - systemTotal,
        actual_vs_system: actualTotal - systemTotal,
        operator_vs_actual: operatorTotal - actualTotal
      }
    }
  }

  const handleSave = async (finalize = false) => {
    if (!cashRegister) return

    setSaving(true)
    setError(null)

    try {
      console.log('[ConferenciaCaixa] Estado atual de reconciliations:', reconciliations)
      
      // Permitir array vazio para caixas sem vendas (backend já valida se há vendas)
      const reconciliationData = reconciliations && reconciliations.length > 0 
        ? reconciliations.map(rec => ({
            payment_receipt_method_id: rec.payment_receipt_method_id,
            operator_informed_amount: rec.operator_informed_amount !== null ? rec.operator_informed_amount : null,
            actual_counted_amount: rec.actual_counted_amount !== null ? rec.actual_counted_amount : null,
            observations: rec.observations || ''
          }))
        : []

      console.log('[ConferenciaCaixa] Salvando conferência:', {
        cashRegisterId: id,
        finalize,
        reconciliationCount: reconciliationData.length,
        reconciliationData
      })

      // Sempre usar saveReconciliation, passando finalize como parâmetro
      const response = await cashRegisterService.saveReconciliation(id, reconciliationData, finalize)

      console.log('[ConferenciaCaixa] Resposta do servidor:', response)

      if (response.success) {
        if (finalize) {
          setFinalizeModalOpen(false)
          setSuccessModalOpen(true)
        } else {
          await loadData() // Recarregar dados
        }
      } else {
        console.error('[ConferenciaCaixa] Erro na resposta:', response)
        setError(response.message || 'Erro ao salvar conferência')
      }
    } catch (error) {
      console.error('[ConferenciaCaixa] Erro ao salvar conferência:', error)
      console.error('[ConferenciaCaixa] Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao salvar conferência. Tente novamente.'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleFinalize = () => {
    setFinalizeModalOpen(true)
  }

  const formatInputValue = (value) => {
    if (value === null || value === undefined || value === '') return ''
    // Formatar como moeda brasileira (mesma lógica do modal de fechamento)
    const numericValue = parseFloat(value)
    if (isNaN(numericValue)) return ''
    return numericValue.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

  const handleActualAmountInputChange = (paymentMethodId, e) => {
    let inputValue = e.target.value
    
    // Remove tudo exceto números (mesma lógica do modal de fechamento)
    let cleaned = inputValue.replace(/\D/g, '')
    
    // Formatar como moeda (mesma lógica do modal de fechamento)
    let formatted = ''
    if (cleaned) {
      formatted = (parseInt(cleaned) / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
    
    // Converter para número para atualizar o estado (remover pontos e substituir vírgula por ponto)
    const numericValue = formatted === '' ? null : formatted.replace(/\./g, '').replace(',', '.')
    const num = numericValue === null ? null : parseFloat(numericValue)
    
    // Atualizar estado
    handleValueChange(paymentMethodId, 'actual_counted_amount', (num === null || isNaN(num)) ? null : num)
    
    // Atualizar o valor do input via evento (para manter controle)
    e.target.value = formatted
  }

  if (loading) {
    return (
      <div className="erp-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados da conferência...</p>
        </div>
      </div>
    )
  }

  if (error && !cashRegister) {
    return (
      <div className="erp-page">
        <div className="error-container">
          <AlertCircle size={48} />
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/erp/vendas/conferencia-caixa')}>
            <ArrowLeft size={16} />
            Voltar
          </button>
        </div>
      </div>
    )
  }

  const isReadOnly = cashRegister?.is_reconciled || false

  return (
    <div className="erp-page conferencia-caixa-detail">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/erp/vendas/caixa')}>
          <ArrowLeft size={16} />
          Voltar
        </button>
        <div>
          <h1>Conferência de Caixa #{cashRegister?.code || id}</h1>
          {isReadOnly && (
            <p className="readonly-notice">✅ Conferência finalizada - Modo somente leitura</p>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Informações Gerais do Caixa */}
      <div className="cash-register-info">
        <h2>Informações Gerais do Caixa</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Código caixa</label>
            <div className="info-value">{cashRegister?.code || '-'}</div>
          </div>
          <div className="info-item">
            <label>Operador</label>
            <div className="info-value">{cashRegister?.opened_by_name || '-'}</div>
          </div>
          <div className="info-item">
            <label>Data abertura</label>
            <div className="info-value">{formatDate(cashRegister?.opening_date)}</div>
          </div>
          <div className="info-item">
            <label>Hora abertura</label>
            <div className="info-value">{formatTime(cashRegister?.opening_date?.substring(11, 19))}</div>
          </div>
          <div className="info-item">
            <label>Data fechamento</label>
            <div className="info-value">{cashRegister?.closing_date ? formatDate(cashRegister.closing_date) : '-'}</div>
          </div>
          <div className="info-item">
            <label>Hora fechamento</label>
            <div className="info-value">{cashRegister?.closing_date ? formatTime(cashRegister.closing_date.substring(11, 19)) : '-'}</div>
          </div>
          <div className="info-item">
            <label>Conferido por</label>
            <div className="info-value">{cashRegister?.reconciled_by_name || 'Não finalizado'}</div>
          </div>
          <div className="info-item">
            <label>Data apuração</label>
            <div className="info-value">{cashRegister?.reconciliation_date ? formatDate(cashRegister.reconciliation_date) : '-'}</div>
          </div>
          <div className="info-item">
            <label>Hora apuração</label>
            <div className="info-value">{cashRegister?.reconciliation_time ? formatTime(cashRegister.reconciliation_time) : '--:--'}</div>
          </div>
        </div>
      </div>

      {/* Tabela de Conferência */}
      <div className="reconciliation-table-container">
        <h2>Tabela de Conferência</h2>
        <div className="table-wrapper">
          <table className="reconciliation-table">
            <thead>
              <tr>
                <th>Forma de Recebimento</th>
                <th>Valor Sistema</th>
                <th>Valor Informado pelo Operador</th>
                <th>Valor Apurado</th>
              </tr>
            </thead>
            <tbody>
              {reconciliations.map((rec) => (
                <tr key={rec.payment_receipt_method_id}>
                  <td className="payment-method-name">{rec.payment_method_name}</td>
                  <td className="system-amount">{formatCurrency(rec.system_amount)}</td>
                  <td>
                    {/* Valor Informado pelo Operador é readonly - vem do fechamento do caixa */}
                    <span className={rec.operator_informed_amount !== null ? '' : 'text-muted'}>
                      {rec.operator_informed_amount !== null ? formatCurrency(rec.operator_informed_amount) : '-'}
                    </span>
                  </td>
                  <td>
                    {isReadOnly ? (
                      <span>{rec.actual_counted_amount !== null ? formatCurrency(rec.actual_counted_amount) : '-'}</span>
                    ) : (
                      <input
                        type="text"
                        className="amount-input"
                        value={formatInputValue(rec.actual_counted_amount)}
                        onChange={(e) => handleActualAmountInputChange(rec.payment_receipt_method_id, e)}
                        placeholder="0,00"
                        inputMode="decimal"
                      />
                    )}
                  </td>
                </tr>
              ))}
              {/* Linha de Totais */}
              {totals && (
                <tr className="totals-row">
                  <td><strong>Totais</strong></td>
                  <td><strong>{formatCurrency(totals.system_total)}</strong></td>
                  <td><strong>{formatCurrency(totals.operator_total)}</strong></td>
                  <td><strong>{formatCurrency(totals.actual_total)}</strong></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seção de Diferenças */}
      {totals && (
        <div className="differences-section">
          <h2>Diferenças</h2>
          <div className="differences-grid">
            <div className="difference-item">
              <label>Operador vs Sistema</label>
              <div className={`difference-value ${totals.differences.operator_vs_system < 0 ? 'negative' : totals.differences.operator_vs_system > 0 ? 'positive' : ''}`}>
                {formatCurrency(totals.differences.operator_vs_system)}
              </div>
            </div>
            <div className="difference-item">
              <label>Apurado vs Sistema</label>
              <div className={`difference-value ${totals.differences.actual_vs_system < 0 ? 'negative' : totals.differences.actual_vs_system > 0 ? 'positive' : ''}`}>
                {formatCurrency(totals.differences.actual_vs_system)}
              </div>
            </div>
            <div className="difference-item">
              <label>Operador vs Apurado</label>
              <div className={`difference-value ${totals.differences.operator_vs_actual < 0 ? 'negative' : totals.differences.operator_vs_actual > 0 ? 'positive' : ''}`}>
                {formatCurrency(totals.differences.operator_vs_actual)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botões de Ação */}
      {!isReadOnly && (
        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => handleSave(false)}
            disabled={saving}
          >
            <Save size={16} />
            {saving ? 'Salvando...' : 'Salvar Rascunho'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleFinalize}
            disabled={saving}
          >
            <CheckCircle size={16} />
            Finalizar Conferência
          </button>
        </div>
      )}

      {/* Modal de Confirmação de Finalização */}
      <Modal
        isOpen={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        title="Finalizar Conferência"
      >
        <div className="finalize-modal-content">
          <p>Tem certeza que deseja finalizar a conferência?</p>
          <p className="warning-text">Após finalizar, não será possível editar os valores.</p>
          {totals && (
            <div className="finalize-summary">
              <h4>Resumo:</h4>
              <p>Total Sistema: <strong>{formatCurrency(totals.system_total)}</strong></p>
              <p>Total Informado: <strong>{formatCurrency(totals.operator_total)}</strong></p>
              <p>Total Apurado: <strong>{formatCurrency(totals.actual_total)}</strong></p>
            </div>
          )}
          <div className="modal-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setFinalizeModalOpen(false)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={() => handleSave(true)}
              disabled={saving}
            >
              {saving ? 'Finalizando...' : 'Confirmar Finalização'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de sucesso após finalizar */}
      <ConfirmModal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false)
          navigate('/erp/vendas/caixa')
        }}
        onConfirm={() => {
          setSuccessModalOpen(false)
          navigate('/erp/vendas/caixa')
        }}
        title="Conferência Finalizada"
        message="Conferência finalizada com sucesso! O caixa foi marcado como conferido e os valores foram registrados."
        type="success"
        confirmText="OK"
        showCancel={false}
      />
    </div>
  )
}

export default ConferenciaCaixaDetailPage

